import React, { useEffect } from 'react';
import { Modal, TextInput, Textarea, Button, Stack, Group, MultiSelect } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import { Goal, CreateGoalData, UpdateGoalData } from '../../types';
import { goalDeadlineDay } from '../../utils/date';

interface GoalModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: CreateGoalData | UpdateGoalData) => void;
  goal?: Goal | null;
  userId: string;
  isLoading?: boolean;
  /** Из чего выбирать компетенции цели (план развития). Нет — поля нет. */
  competencyOptions?: { value: string; label: string }[];
  /** Новая цель сразу под эти компетенции (кнопка «Поставить цель» в плане). */
  initialCompetencyIds?: string[];
}

export const GoalModal: React.FC<GoalModalProps> = ({
  opened,
  onClose,
  onSubmit,
  goal,
  userId,
  isLoading = false,
  competencyOptions,
  initialCompetencyIds,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  // `YYYY-MM-DD`, как его отдаёт Mantine 8: срок — день в поясе компании, а не момент.
  const [deadline, setDeadline] = React.useState<string | null>(null);
  const [competencyIds, setCompetencyIds] = React.useState<string[]>([]);

  const isEditing = !!goal;
  // Цель — тому, чей профиль открыт; постановщик — текущий пользователь (ставит бэкенд).
  const assigneeId = goal?.assignee._id ?? userId;

  useEffect(() => {
    if (opened) {
      if (goal) {
        setTitle(goal.title);
        setDescription(goal.description);
        setDeadline(goalDeadlineDay(goal) ?? null);
        setCompetencyIds((goal.competencies ?? []).map((c) => c._id));
      } else {
        setTitle('');
        setDescription('');
        setDeadline(null);
        setCompetencyIds(initialCompetencyIds ?? []);
      }
    }
  }, [opened, goal, userId, initialCompetencyIds]);

  const handleSubmit = () => {
    if (!title.trim() || !description.trim() || !assigneeId) {
      return;
    }

    const data = {
      title: title.trim(),
      description: description.trim(),
      assigneeId,
      // Очищенный срок при правке — null: иначе поле не уходит и сервер срок не снимает.
      deadline: deadline ?? (isEditing ? null : undefined),
      // Без списка компетенций поле не показано — и не трогаем то, что уже записано.
      ...(competencyOptions ? { competencyIds } : {}),
    };

    onSubmit(data);
  };

  const isFormValid = title.trim() && description.trim() && assigneeId;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? t('goals.editGoal') : t('goals.createGoal')}
      size="md"
    >
      <Stack gap="md">
        <TextInput
          label={t('goals.title')}
          placeholder={t('goals.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          required
        />

        <Textarea
          label={t('goals.description')}
          placeholder={t('goals.descriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={3}
          required
        />

        <DateInput
          label={t('goals.deadline')}
          placeholder={t('goals.deadlinePlaceholder')}
          value={deadline}
          onChange={setDeadline}
          minDate={new Date()}
          clearable
        />

        {competencyOptions && competencyOptions.length > 0 && (
          <MultiSelect
            label={t('goals.competencies')}
            description={t('goals.competenciesHint')}
            data={competencyOptions}
            value={competencyIds}
            onChange={setCompetencyIds}
            searchable
            clearable
          />
        )}

        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="light" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isFormValid || isLoading} loading={isLoading}>
            {isEditing ? t('common.save') : t('common.create')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
